import { useCallback, useMemo, useState } from "react";

import { Icon_BottomBar24, useBottomSheetDrag, useModalManager } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import { webAppURL } from "../../config";
import { ModalIds } from "../../constants/modalIds";
import useNavigation from "../../hooks/navigation";
import { useProject } from "../../hooks/useProject";
import { useProjectOrWebExport } from "../../hooks/useProjectOrWebExport";
import { useTeamActiveSubscription } from "../../hooks/useTeamActiveSubscription";
import { useTracker } from "../../hooks/useTracker";
import { useViewLinkId, useWebExport } from "../../hooks/useWebExport";
import { ProjectRoleUtils, SubscriptionPeriod } from "../../types";
import { handleFileDownload } from "../../utils/handleFileDownload";
import { DeepLinkModal } from "../DeepLinkModal/DeepLinkModal";
import { OverlayProvider } from "../Headers/components/ShareModal/components/MenuPortal";
import { PaywallModal } from "../Headers/components/ShareModal/components/PaywallModal";
import { Toolbar } from "../Toolbar/Toolbar";
import { ToolButton } from "../ToolButton/ToolButton";
import { ProjectMetadata } from "./ProjectMetadata/ProjectMetadata";
import { ProjectVersionSelector } from "./ProjectVersionSelector/ProjectVersionSelector";

import style from "./ProjectSidebarMobile.module.css";

interface ProjectSidebarMobileProps {
  isOpen: boolean;
  setOpen?: (open: boolean) => void;
  isCommentingEnabled: boolean;
}

export const ProjectSidebarMobile = ({
  isOpen,
  setOpen,
  isCommentingEnabled,
}: ProjectSidebarMobileProps) => {
  const { data: projectData, isLoading: isProjectLoading } = useProject();
  const { data: webExportData } = useWebExport();
  const viewLinkId = useViewLinkId();
  const { data: projectOrWebExportData } = useProjectOrWebExport();
  const { projectId = "", drawingId = "" } = useParams();

  const navigation = useNavigation();

  const { open: openModal } = useModalManager();
  const openShareModal = () => openModal({ id: ModalIds.SHARE_PROJECT });

  const tracker = useTracker();

  const { data: hasTeamSubscription } = useTeamActiveSubscription(projectData?.teamId);

  const [isOwnerPaywallOpen, setIsOwnerPaywallOpen] = useState(false);
  const [isNonOwnerPaywallOpen, setIsNonOwnerPaywallOpen] = useState(false);

  const handleCopyLink = () => {
    const url = `${webAppURL}/p/${projectOrWebExportData?.projectId}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success("Copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  const shouldShowDownloadButton =
    projectData?.latestRevision.fileDownload?.url && projectData.project.projectId;

  const paywallReturnURL = useMemo(() => window.location.href, []);

  const handleDownloadClick = useCallback(() => {
    if (!projectData) return;

    tracker.trackDownloadFileClicked({
      projectTeamId: projectData.teamId,
      projectId: projectData.project.projectId,
    });

    if (hasTeamSubscription) {
      handleFileDownload(projectData?.latestRevision.fileDownload?.url);
      return;
    }

    const isOwner = ProjectRoleUtils.isAtLeast(projectData.userRole, "owner");
    if (isOwner) {
      setIsOwnerPaywallOpen(true);
    } else {
      setIsNonOwnerPaywallOpen(true);
    }

    tracker.trackPaywallModalShown({
      remoteProjectId: projectData.project.projectId,
      paywallReason: "download_shapr_file",
      hasSubscribeAction: isOwner,
    });
  }, [tracker, projectData, hasTeamSubscription]);

  const canShare =
    projectOrWebExportData?.userRole &&
    ProjectRoleUtils.isAtLeast(projectOrWebExportData.userRole, "editor");

  const handleMetadataMenuOpen = () => {
    tracker.trackContextMenuOpened({
      location: "project_page_project_title",
    });
  };

  const handleMetadataCopyLink = () => {
    tracker.trackContextMenuActed({
      action: "copy_link",
      location: "project_page_project_title",
    });
    handleCopyLink();
  };

  const handleMetadataShare = () => {
    tracker.trackContextMenuActed({
      action: "share",
      location: "project_page_project_title",
    });
    openShareModal();
  };

  const handleMetadataDownload = () => {
    tracker.trackContextMenuActed({
      action: "download",
      location: "project_page_project_title",
    });
    handleDownloadClick();
  };

  const { sheetRef, handleProps } = useBottomSheetDrag({
    onClose: () => setOpen?.(false),
  });

  return (
    <>
      <div className={style.controlButton}>
        <ToolButton
          alt="Project Outline"
          title="Project Outline"
          icon={<Icon_BottomBar24 />}
          onClick={() => {
            const newState = isOpen ? "close" : "open";
            tracker.trackProjectSidebarToggled({ newState });
            setOpen?.(!isOpen);
          }}
          isActive={isOpen}
          hideShadow
        />
      </div>
      {!drawingId && isCommentingEnabled && (
        <div className={style.toolbarWrapper}>
          <Toolbar />
        </div>
      )}

      <div ref={sheetRef} className={classNames(style.bottomSheet, { [style.isOpen]: isOpen })}>
        <div
          className={style.handle}
          onClick={() => {
            const newState = isOpen ? "close" : "open";
            tracker.trackProjectSidebarToggled({ newState });
            setOpen?.(!isOpen);
          }}
          {...handleProps}
        >
          <div className={style.handleBar} />
        </div>

        <div className={style.content}>
          {projectOrWebExportData && (
            <>
              <ProjectMetadata
                isLoading={isProjectLoading}
                teamId={projectOrWebExportData.teamId}
                spaceName={projectOrWebExportData.spaceName}
                projectName={projectOrWebExportData.projectName}
                lastModifiedAt={projectOrWebExportData.lastModifiedAt}
                createdByUser={projectOrWebExportData.createdByUser}
                showEditInAppButton={ProjectRoleUtils.isAtLeast(
                  projectOrWebExportData.userRole,
                  "editor"
                )}
                onMenuOpen={handleMetadataMenuOpen}
                onCopyLink={handleMetadataCopyLink}
                onShare={handleMetadataShare}
                onDownload={handleMetadataDownload}
                canShare={canShare}
                canDownload={Boolean(shouldShowDownloadButton)}
              />
              <div className={style.divider}></div>
            </>
          )}

          <OverlayProvider>
            <div className={style.versionsWrapper}>
              <ProjectVersionSelector />
              {webExportData && (
                <div className={style.modelList}>
                  <button
                    className={classNames([style.listItem, !drawingId && style.active])}
                    onClick={() => {
                      navigation.goToViewLink({
                        viewLinkId: viewLinkId ?? "",
                        projectId,
                      });
                      setOpen?.(false);
                    }}
                  >
                    {projectOrWebExportData?.thumbnailUrl && (
                      <div className={style.listItemIcon}>
                        <img
                          src={projectOrWebExportData?.thumbnailUrl}
                          alt="Model Thumbnail"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </div>
                    )}

                    <div>Model</div>
                  </button>
                </div>
              )}
              {webExportData?.webExport.drawingsDownload?.length && (
                <div className={style.drawingsList}>
                  <div className={style.drawingsTitle}>Drawings</div>

                  {webExportData?.webExport.drawingsDownload.map((drawing) => (
                    <button
                      key={drawing.drawingId}
                      className={classNames([
                        style.listItem,
                        drawing.drawingId === drawingId && style.active,
                      ])}
                      onClick={() => {
                        navigation.goToDrawing({
                          viewLinkId: viewLinkId ?? "",
                          projectId,
                          drawingId: drawing.drawingId,
                        });
                        setOpen?.(false);
                      }}
                    >
                      <div>{drawing.title}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </OverlayProvider>
        </div>
      </div>

      <DeepLinkModal />
      <PaywallModal
        modalProps={{ open: isOwnerPaywallOpen, onClose: () => setIsOwnerPaywallOpen(false) }}
        title={"Subscribe to export Shapr3D"}
        description={"Get instant access to all features without limits."}
        showSubscriptionButton={true}
        onClose={() => {
          setIsOwnerPaywallOpen(false);
          if (projectData) {
            tracker.trackPaywallModalDismissed({
              remoteProjectId: projectData.project.projectId,
              paywallReason: "download_shapr_file",
            });
          }
        }}
        onClick={() => {
          if (projectData) {
            navigation.goToCheckout({
              period: SubscriptionPeriod.Yearly,
              showPeriodPicker: true,
              utmSource: "download_shapr_file",
              returnToURL: paywallReturnURL,
            });
            tracker.trackPaywallSubscribeClicked({
              paywallReason: "download_shapr_file",
              remoteProjectId: projectData.project.projectId,
            });
          }
        }}
      />
      <PaywallModal
        modalProps={{
          open: isNonOwnerPaywallOpen,
          onClose: () => setIsNonOwnerPaywallOpen(false),
        }}
        title={"Your current team plan doesn't include this feature."}
        size="medium"
        description={
          "Contact your team owner to upgrade the subscription and gain access to all Pro features."
        }
        onClose={() => {
          setIsNonOwnerPaywallOpen(false);
          if (projectData) {
            tracker.trackPaywallModalDismissed({
              remoteProjectId: projectData.project.projectId,
              paywallReason: "download_shapr_file",
            });
          }
        }}
        showSubscriptionButton={false}
      />
    </>
  );
};
